// Reuse audit: CategoriesPage and MerchantsPage previously duplicated their
// scope/offline/status shells but had no aggregation renderer to reuse. This
// mode-parameterized surface keeps the server-state matrix in one place.
import type { UseQueryResult } from '@tanstack/react-query';
import { AmountText } from '@/components/AmountText';
import type {
  SpendAggregationGroup,
  SpendAggregationResponse,
} from './spendAggregation';

type SpendQuery = Pick<UseQueryResult<SpendAggregationResponse>, 'data' | 'isPending' | 'isFetching' | 'isPlaceholderData' | 'isError' | 'refetch'>;

export type SpendAggregationMode = 'category' | 'merchant';

export function SpendAggregationSurface({
  mode,
  spend,
  onDrilldown,
}: {
  mode: SpendAggregationMode;
  spend: SpendQuery;
  onDrilldown?: (descriptor: SpendAggregationGroup['drilldown']) => void;
}) {
  const noun = mode === 'category' ? 'category' : 'merchant';
  const title = mode === 'category' ? 'Spend by category' : 'Spend by merchant';

  if (spend.isError) {
    return (
      <section aria-labelledby={`${mode}-spend-state-heading`} className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4" data-testid={`spend-aggregation-${mode}`}>
        <h2 id={`${mode}-spend-state-heading`} className="font-semibold">Could not load {noun} spend.</h2>
        <div role="status" aria-live="polite" className="mt-1 text-sm text-degraded-foreground">Could not load {noun} spend. No aggregation values are available.</div>
        <button type="button" className="mt-3 min-h-11 rounded-md border border-input px-3 underline" onClick={() => { void spend.refetch(); }}>Retry</button>
      </section>
    );
  }

  if (spend.data === undefined) {
    return (
      <section aria-labelledby={`${mode}-spend-state-heading`} className="mt-4 rounded-md border border-border bg-muted/30 p-4" data-testid={`spend-aggregation-${mode}`}>
        <h2 id={`${mode}-spend-state-heading`} className="sr-only">{title}</h2>
        <p role="status" aria-live="polite">{spend.isPending || spend.isFetching ? `Loading ${noun} spend…` : `No ${noun} spend data available.`}</p>
      </section>
    );
  }

  const { data, meta } = spend.data;
  if (meta.coverage.status !== 'ready') {
    const message = meta.coverage.status === 'incomplete'
      ? `${noun[0].toUpperCase()}${noun.slice(1)} spend is incomplete.`
      : `${noun[0].toUpperCase()}${noun.slice(1)} spend is unavailable.`;
    return (
      <section aria-labelledby={`${mode}-spend-state-heading`} className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4" data-testid={`spend-aggregation-${mode}`}>
        <h2 id={`${mode}-spend-state-heading`} className="font-semibold">{title}</h2>
        <p role="status" aria-live="polite" className="mt-1 text-sm text-degraded-foreground">{message} Numeric details are withheld.</p>
      </section>
    );
  }

  const total = data.reconciliation.total;
  const hasReadyTotal = total.status === 'single';

  return (
    <section aria-labelledby={`${mode}-spend-heading`} className="mt-4" data-testid={`spend-aggregation-${mode}`}>
      <header className="rounded-lg border border-border bg-card p-4">
        <h2 id={`${mode}-spend-heading`} className="text-lg font-semibold">{title}</h2>
        {spend.isPlaceholderData && (
          <p role="status" aria-live="polite" className="mt-1 text-sm text-muted-foreground">Updating this scope; showing the previous result until it arrives.</p>
        )}
        {hasReadyTotal ? (
          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-sm text-muted-foreground">Total spend</span>
            <AmountText value={total.amount} currency={total.currency} />
          </div>
        ) : (
          <p role="status" aria-live="polite" className="mt-3 text-sm text-degraded-foreground">Total unavailable.</p>
        )}
        {data.groups.length === 0 && hasReadyTotal && <p className="mt-2 text-sm text-muted-foreground">No spend in this range.</p>}
        {hasReadyTotal && data.pending_transfer_proposals.status === 'included' && data.pending_transfer_proposals.count !== null && (
          <p className="mt-3 text-sm text-muted-foreground" role="note">
            {`Includes ${data.pending_transfer_proposals.count} pending transfer proposal${data.pending_transfer_proposals.count === 1 ? '' : 's'}.`}
          </p>
        )}
      </header>

      {data.groups.length > 0 && (
        <ul aria-label={`${title} results`} className="mt-3 space-y-2" data-testid="spend-aggregation-groups">
          {data.groups.map((group) => {
            return (
              <li key={group.key} className="rounded-lg border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <button
                    type="button"
                    className="ms-tap-44 min-h-11 text-left font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`View ${group.label} transactions`}
                    onClick={() => onDrilldown?.(group.drilldown)}
                  >
                    {group.label}
                  </button>
                  {group.total.status === 'single' ? <AmountText value={group.total.amount} currency={group.total.currency} /> : <span className="text-sm text-degraded-foreground">Amount unavailable.</span>}
                </div>
                {group.total.status === 'single' && group.share !== null && <p className="mt-2 text-sm text-muted-foreground">Share: {group.share}</p>}
                {group.total.status === 'single' && group.line_item_count !== null && <p className="text-sm text-muted-foreground">{`${group.line_item_count} line item${group.line_item_count === 1 ? '' : 's'}`}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default SpendAggregationSurface;
